/* MenuGuide — universal patient-friendly tour за всяко меню.
 *
 *  Reuses pattern from DiaryGuide.jsx, but accepts external `modules` array
 *  и `storageKey` за first-visit detection. Floating bubble + step modal +
 *  AI badge + "Покажи ми" jump-to-anchor.
 *
 *  Регулаторно: всички медицински фрази остават с patient-safe рамка.
 */
import React, { useEffect, useState } from "react";
import {
  Compass, X, ChevronRight, ChevronLeft, Sparkles,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

/**
 * Module shape:
 * {
 *   id: string,
 *   icon: LucideIcon,
 *   color: hex string,
 *   name_bg, name_en,
 *   purpose_bg, purpose_en,
 *   benefit_bg, benefit_en,
 *   action_bg, action_en,
 *   aiPowered?: bool,
 *   jumpId?: string,   // optional DOM element id
 * }
 */
export default function MenuGuide({
  modules,
  storageKey,
  triggerLabelBg = "Ръководство",
  triggerLabelEn = "Guide",
  triggerColorClass = "from-emerald-500 to-cyan-500",
  triggerPosition = "bottom-24 left-4 md:left-6",
  autoOpenFirstTime = true,
  testIdPrefix = "menu-guide",
}) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  const [hasBeenSeen, setHasBeenSeen] = useState(false);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const seen = localStorage.getItem(storageKey);
      if (seen) setHasBeenSeen(true);
    } catch { /* ignore */ }
  }, [storageKey]);

  useEffect(() => {
    if (!autoOpenFirstTime || !storageKey) return;
    try {
      const seen = localStorage.getItem(storageKey);
      if (!seen) {
        const t = setTimeout(() => setOpen(true), 1500);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, [autoOpenFirstTime, storageKey]);

  const close = () => {
    setOpen(false);
    setHasBeenSeen(true);
    if (storageKey) {
      try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    }
  };

  if (!modules || modules.length === 0) return null;
  const safeStep = Math.min(step, modules.length - 1);
  const currentModule = modules[safeStep];
  const Icon = currentModule.icon;

  const goToModule = () => {
    if (currentModule.jumpId) {
      const el = document.getElementById(currentModule.jumpId);
      if (el) {
        close();
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          el.classList.add("ring-2", "ring-violet-400", "ring-offset-2", "transition-all");
          setTimeout(() => el.classList.remove("ring-2", "ring-violet-400", "ring-offset-2"), 2000);
        }, 200);
      }
    }
  };

  return (
    <>
      {/* iter351.F: Hide floating trigger after first view (per user request).
          Still openable via menu/profile entry if needed in future. */}
      {!hasBeenSeen && (
        <button
          type="button"
          onClick={() => { setStep(0); setOpen(true); }}
          data-testid={`${testIdPrefix}-trigger`}
          aria-label={bg ? triggerLabelBg : triggerLabelEn}
          className={`fixed ${triggerPosition} z-40 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold bg-gradient-to-br ${triggerColorClass} text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all pb-[calc(0.625rem+env(safe-area-inset-bottom,0px))]`}
        >
          <Compass className="w-4 h-4" />
          {bg ? triggerLabelBg : triggerLabelEn}
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
          onClick={close}
          data-testid={`${testIdPrefix}-modal`}
        >
          <div
            className={`w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden ${
              isDark ? "bg-[hsl(223,47%,10%)] border border-[hsl(223,47%,20%)]" : "bg-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-5 py-4 flex items-center justify-between gap-3"
              style={{
                background: `linear-gradient(135deg, ${currentModule.color} 0%, ${currentModule.color}dd 100%)`,
              }}
            >
              <div className="flex items-center gap-3 text-white">
                <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider opacity-90 font-semibold">
                    {bg ? `Стъпка ${safeStep + 1} от ${modules.length}` : `Step ${safeStep + 1} of ${modules.length}`}
                  </div>
                  <h3 className="text-base font-bold leading-tight line-clamp-2">
                    {bg ? currentModule.name_bg : currentModule.name_en}
                  </h3>
                </div>
              </div>
              <button
                onClick={close}
                className="p-1.5 rounded-lg hover:bg-white/20 text-white"
                aria-label={bg ? "Затваряне" : "Close"}
                data-testid={`${testIdPrefix}-close`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-3" data-testid={`${testIdPrefix}-step-${currentModule.id}`}>
              {currentModule.aiPowered && (
                <div
                  className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${
                    isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-50 text-violet-700"
                  }`}
                >
                  <Sparkles className="w-3 h-3" />
                  {bg ? "С помощта на изкуствен интелект" : "Powered by AI"}
                </div>
              )}

              <div>
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                  {bg ? "Какво е това" : "What it is"}
                </p>
                <p className={`text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  {bg ? currentModule.purpose_bg : currentModule.purpose_en}
                </p>
              </div>

              <div>
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-cyan-300" : "text-cyan-700"}`}>
                  {bg ? "С какво Ви помага" : "How it helps you"}
                </p>
                <p className={`text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  {bg ? currentModule.benefit_bg : currentModule.benefit_en}
                </p>
              </div>

              <div>
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  {bg ? "Какво да направите" : "What to do"}
                </p>
                <p className={`text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  {bg ? currentModule.action_bg : currentModule.action_en}
                </p>
              </div>

              {currentModule.jumpId && (
                <button
                  onClick={goToModule}
                  className={`w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold transition-colors ${
                    isDark
                      ? "bg-[hsl(223,47%,15%)] text-white hover:bg-[hsl(223,47%,20%)]"
                      : "bg-slate-100 text-slate-800 hover:bg-slate-200"
                  }`}
                  data-testid={`${testIdPrefix}-jump-btn`}
                >
                  {bg ? "Покажете ми този раздел" : "Take me to this module"}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>

            <div
              className={`px-5 py-3 border-t flex items-center justify-between gap-3 ${
                isDark ? "border-[hsl(223,47%,20%)] bg-[hsl(223,47%,8%)]" : "border-gray-100 bg-slate-50"
              }`}
            >
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={safeStep === 0}
                className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 ${
                  safeStep === 0
                    ? "opacity-30 cursor-not-allowed"
                    : (isDark ? "text-gray-300 hover:bg-white/5" : "text-gray-600 hover:bg-gray-100")
                }`}
                data-testid={`${testIdPrefix}-prev`}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                {bg ? "Назад" : "Back"}
              </button>

              <div className="flex gap-1.5 flex-wrap justify-center max-w-[60%]">
                {modules.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setStep(i)}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      i === safeStep
                        ? "bg-violet-500 scale-150"
                        : (isDark ? "bg-gray-700" : "bg-gray-300")
                    }`}
                    aria-label={`${bg ? "Стъпка" : "Step"} ${i + 1}`}
                  />
                ))}
              </div>

              {safeStep === modules.length - 1 ? (
                <button
                  onClick={close}
                  className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 bg-gradient-to-r ${triggerColorClass} text-white shadow-md`}
                  data-testid={`${testIdPrefix}-finish`}
                >
                  {bg ? "Разбрах" : "I'm ready"}
                </button>
              ) : (
                <button
                  onClick={() => setStep((s) => Math.min(modules.length - 1, s + 1))}
                  className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 ${
                    isDark ? "bg-violet-500/20 text-violet-200 hover:bg-violet-500/30" : "bg-violet-100 text-violet-700 hover:bg-violet-200"
                  }`}
                  data-testid={`${testIdPrefix}-next`}
                >
                  {bg ? "Напред" : "Next"}
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div
              className={`px-5 py-2.5 text-[10px] leading-relaxed text-center ${
                isDark ? "text-gray-500 bg-[hsl(223,47%,6%)]" : "text-gray-400 bg-white border-t border-gray-100"
              }`}
            >
              {bg
                ? "Образователен инструмент. Не замества лекарска консултация."
                : "Educational tool. Does not replace medical consultation."}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
